import { chains } from "@gearbox-protocol/sdk";
import { Batch, shortenHash } from "@gearbox-protocol/sdk/permissionless";
import {
  Button,
  CopyButton,
  ExpandableCard,
  formatTimestamp,
  useIsMobile,
} from "@gearbox-protocol/ui-kit";
import { ExternalLink } from "lucide-react";
import { useAccount } from "wagmi";
import { useSignProposal } from "@/hooks/use-sign-proposal";
import { ProposalCall } from "./proposal-call";
import { ProposalSignatures } from "./proposal-signatures";

interface ProposalCardProps {
  proposal: Batch;
  isQueue?: boolean;
}

export function ProposalCard({ proposal, isQueue = false }: ProposalCardProps) {
  const { address } = useAccount();
  const { canSign, sign, isSigning, parsedCalls } = useSignProposal(proposal);
  const isMobile = useIsMobile();
  const explorerUrl = chains.Mainnet.blockExplorers?.default?.url;

  return (
    <ExpandableCard
      header={
        <div className="flex flex-col md:flex-row items-top justify-between gap-2 md:gap-0">
          <div className="flex items-center gap-4 flex-wrap">
            <span className="text-muted-foreground font-mono shrink-0">
              #{shortenHash(proposal.hash)}
            </span>
            <div className="flex items-center">
              <span className="text-foreground">{proposal.name}</span>
            </div>
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            <span className="text-muted-foreground min-w-[80px] md:text-right">
              {proposal.calls.length} actions
            </span>
            <span className="text-muted-foreground min-w-[140px] md:text-right">
              {proposal.timestamp ? formatTimestamp(proposal.timestamp) : "N/A"}
            </span>
            {isQueue ? (
              proposal.signatures.find(
                s => s.signer.toLowerCase() === address?.toLowerCase(),
              ) ? (
                <span className="flex items-center text-foreground gap-1">
                  <span className="text-2xl">•</span>
                  <span>Signed</span>
                </span>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={async e => {
                    e.stopPropagation();
                    await sign();
                  }}
                  disabled={!canSign || isSigning}
                  className="px-6 bg-transparent border border-success text-success hover:bg-success/10 disabled:border-muted disabled:text-muted-foreground disabled:hover:bg-transparent"
                >
                  {isSigning ? "Signing..." : "Confirm"}
                </Button>
              )
            ) : (
              <div className="flex items-center gap-4">
                <span className="flex items-center text-foreground gap-1">
                  <span className="text-2xl">•</span>
                  <span>
                    {proposal.transactionHash !== undefined
                      ? "Executed"
                      : "Pending"}
                  </span>
                </span>
                {proposal.transactionHash && explorerUrl && (
                  <div className="mx-[-12px]">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={e => {
                        e.stopPropagation();
                        window.open(
                          `${explorerUrl}${
                            explorerUrl.endsWith("/") ? "" : "/"
                          }tx/${proposal.transactionHash}`,
                          "_blank",
                        );
                      }}
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-[1fr_minmax(420px,max-content)] md:gap-12 gap-4 overflow-x-auto">
        <div className="space-y-2">
          <div className="flex w-full items-center justify-between gap-2">
            <span className="text-muted-foreground whitespace-nowrap">
              Proposal hash:
            </span>
            <div className="flex items-center gap-1 font-mono text-sm">
              {isMobile ? shortenHash(proposal.hash) : proposal.hash}
              <CopyButton text={proposal.hash} name="Hash" />
            </div>
          </div>
          <div className="flex w-full items-center justify-between gap-2">
            <span className="text-muted-foreground whitespace-nowrap">
              Prev hash:
            </span>
            <div className="flex items-center gap-1 font-mono text-sm">
              {isMobile ? shortenHash(proposal.prevHash) : proposal.prevHash}
              <CopyButton text={proposal.prevHash} name="Hash" />
            </div>
          </div>

          <div className="md:space-y-2">
            <span className="text-muted-foreground">Calls:</span>
            {parsedCalls.map((call, index) => (
              <ProposalCall
                // biome-ignore lint/suspicious/noArrayIndexKey: <Allow index as key for indistinguishable rows>
                key={`call-${index}`}
                index={index + 1}
                call={call}
              />
            ))}
          </div>
        </div>

        <div className="border-l border-input pl-8">
          <ProposalSignatures
            signers={proposal.signatures.map(t => t.signer) || []}
            isExecuted={proposal.transactionHash !== undefined}
          />
        </div>
      </div>
    </ExpandableCard>
  );
}
